import { useCallback, useEffect, useState } from "react";

import {
  formatDateTime,
  formatInt,
} from "@kontextwerk/web-common";

import EstimationMethodBadge from "../components/EstimationMethodBadge";
import FilterBar from "../components/FilterBar";
import { fetchProviderHealth } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type { CostFilters, ProviderHealthResponse } from "../lib/types";

export default function ProvidersRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(30));
  // Filter component shares a uniform shape; only date-range is honoured for
  // this endpoint (the others have no effect server-side).
  const [filters, setFilters] = useState<CostFilters>({});
  const [data, setData] = useState<ProviderHealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchProviderHealth(range.from, range.to);
      setData(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Provider-Pricing-Health</h1>
        <span className="text-xs text-kw-text-faint">
          {range.from} → {range.to}
        </span>
      </header>
      <p className="text-xs text-kw-text-faint max-w-3xl">
        Listet alle Provider × Model im Zeitraum. Pending-Counts zeigen
        msg_cloud_ai-Modelle ohne Pricing-Entry — Operator füllt sie via
        <span className="font-mono"> PUT /v1/admin/msg-cloud-ai-pricing </span>
        und triggert <span className="font-mono">POST /v1/admin/msg-cloud-ai-backfill</span>.
      </p>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <div className="bg-kw-bg-elevated border border-kw-border-subtle rounded p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-kw-text-muted">Pending-Calls insgesamt</span>
          <span
            className={`text-2xl font-medium font-mono ${
              (data?.total_pending ?? 0) > 0 ? "text-yellow-300" : "text-green-300"
            }`}
          >
            {formatInt(data?.total_pending ?? 0)}
          </span>
        </div>
      </div>

      <section className="bg-kw-bg-elevated border border-kw-border-subtle rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-kw-bg-overlay text-kw-text-muted text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Provider</th>
              <th className="px-3 py-2 text-left font-medium">Model</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-right font-medium">Priced</th>
              <th className="px-3 py-2 text-right font-medium">Pending</th>
              <th className="px-3 py-2 text-left font-medium">Methods</th>
              <th className="px-3 py-2 text-left font-medium">Last Call</th>
            </tr>
          </thead>
          <tbody>
            {(data?.entries ?? []).map((e) => (
              <tr
                key={`${e.provider}/${e.model}`}
                className="border-t border-kw-border-subtle"
              >
                <td className="px-3 py-2 font-mono">{e.provider}</td>
                <td className="px-3 py-2 font-mono">{e.model}</td>
                <td className="px-3 py-2 text-right">{formatInt(e.total_calls)}</td>
                <td className="px-3 py-2 text-right text-green-300">
                  {formatInt(e.priced_calls)}
                </td>
                <td
                  className={`px-3 py-2 text-right ${
                    e.pending_calls > 0 ? "text-yellow-300" : ""
                  }`}
                >
                  {formatInt(e.pending_calls)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {e.estimation_methods.map((m) => (
                      <EstimationMethodBadge key={m} method={m} />
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-[11px] font-mono">
                  {formatDateTime(e.last_call_at)}
                </td>
              </tr>
            ))}
            {(data?.entries ?? []).length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-kw-text-faint"
                >
                  Keine Provider-Calls im Zeitraum.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
